/**
 * ScheduleForm — create / edit-future form for a client's medication
 * schedule (spec §9.3, §13.4). One field set for both modes:
 * name, type, dosage, quantity, start/end date, time, timezone,
 * recurrence (+ weekdays / day-of-month) and instructions.
 *
 * Edits apply to FUTURE occurrences only; past intake history is never
 * touched (repository enforces this — updateSchedule never rewrites
 * intake events). Pure logic lives in ./scheduleFormLogic (unit tested).
 */
import { useMemo, useState } from 'react'
import {
  Button,
  DateField,
  SegmentedControl,
  Select,
  Stepper,
  TextArea,
  TextField,
  TimeField,
  WeekdaySelector,
} from '@/components/ui'
import type { HealthItemType, RecurrenceType } from '@/types'
import {
  validateScheduleValues,
  type ScheduleFormErrors,
  type ScheduleFormValues,
} from './scheduleFormLogic'

export type { ScheduleFormErrors, ScheduleFormValues } from './scheduleFormLogic'
export {
  emptyScheduleValues,
  recurrenceRuleFromValues,
  validateScheduleValues,
  valuesFromSchedule,
  valuesToNewSchedule,
} from './scheduleFormLogic'

const TYPE_OPTIONS: { value: HealthItemType; label: string }[] = [
  { value: 'medication', label: 'Medicatie' },
  { value: 'supplement', label: 'Supplement' },
  { value: 'other', label: 'Overig' },
]

const RECURRENCE_OPTIONS: { value: RecurrenceType; label: string }[] = [
  { value: 'once', label: 'Eenmalig' },
  { value: 'daily', label: 'Dagelijks' },
  { value: 'weekly', label: 'Wekelijks' },
  { value: 'monthly', label: 'Maandelijks' },
]

function timezoneOptions(): { value: string; label: string }[] {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam'
  const known = ['Europe/Amsterdam', 'Europe/Brussels', 'Europe/London', 'UTC']
  const all = [detected, ...known.filter((zone) => zone !== detected)]
  return all.map((zone) => ({ value: zone, label: zone.replace('_', ' ') }))
}

export type ScheduleFormProps = {
  initialValues: ScheduleFormValues
  submitLabel: string
  saving: boolean
  /** Shown in edit mode: changes never rewrite history. */
  editMode?: boolean
  onSubmit: (values: ScheduleFormValues) => void
  onCancel: () => void
}

export function ScheduleForm({ initialValues, submitLabel, saving, editMode = false, onSubmit, onCancel }: ScheduleFormProps) {
  const [values, setValues] = useState<ScheduleFormValues>(initialValues)
  const [errors, setErrors] = useState<ScheduleFormErrors>({})
  const zones = useMemo(timezoneOptions, [])

  const patch = (partial: Partial<ScheduleFormValues>) => {
    setValues((prev) => ({ ...prev, ...partial }))
  }

  const submit = () => {
    const nextErrors = validateScheduleValues(values)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    onSubmit(values)
  }

  return (
    <div className="caregiver-form">
      {editMode ? (
        <p className="caregiver-muted">
          Wijzigingen gelden alleen voor toekomstige medicatiemomenten. De geschiedenis van
          eerdere innames wordt nooit aangepast.
        </p>
      ) : null}

      <TextField
        label="Naam"
        value={values.name}
        onChange={(name) => patch({ name })}
        error={errors.name}
        required
        placeholder="Bijvoorbeeld: Vitamine D"
      />
      <Select
        label="Type"
        value={values.healthType}
        onChange={(healthType) => patch({ healthType: healthType as HealthItemType })}
        options={TYPE_OPTIONS}
      />
      <div className="caregiver-form-row">
        <TextField
          label="Dosering"
          value={values.dosage}
          onChange={(dosage) => patch({ dosage })}
          placeholder="Bijvoorbeeld: 10 mg"
        />
        <Stepper
          label="Aantal"
          value={values.quantity}
          onChange={(quantity) => patch({ quantity })}
          min={1}
          max={10}
          unit="stuks"
        />
      </div>
      <div className="caregiver-form-row">
        <DateField
          label="Startdatum"
          value={values.startDate}
          onChange={(startDate) => patch({ startDate })}
          error={errors.startDate}
        />
        <DateField
          label="Einddatum (optioneel)"
          value={values.endDate}
          onChange={(endDate) => patch({ endDate })}
          error={errors.endDate}
        />
      </div>
      <div className="caregiver-form-row">
        <TimeField
          label="Tijdstip"
          value={values.localTime}
          onChange={(localTime) => patch({ localTime })}
          error={errors.localTime}
        />
        <Select
          label="Tijdzone"
          value={values.timezone}
          onChange={(timezone) => patch({ timezone })}
          options={zones}
        />
      </div>
      <SegmentedControl
        label="Herhaling"
        value={values.recurrenceType}
        onChange={(recurrenceType) => patch({ recurrenceType: recurrenceType as RecurrenceType })}
        options={RECURRENCE_OPTIONS}
      />
      {values.recurrenceType === 'weekly' ? (
        <div>
          <WeekdaySelector value={values.weekdays} onChange={(weekdays) => patch({ weekdays })} />
          {errors.weekdays ? (
            <p className="medication-error" role="alert">
              {errors.weekdays}
            </p>
          ) : null}
        </div>
      ) : null}
      {values.recurrenceType === 'monthly' ? (
        <Stepper
          label="Dag van de maand"
          value={values.dayOfMonth}
          onChange={(dayOfMonth) => patch({ dayOfMonth })}
          min={1}
          max={31}
        />
      ) : null}
      {values.recurrenceType === 'monthly' ? (
        <p className="caregiver-muted">
          Bestaat de dag niet in een maand (bijvoorbeeld 31 februari), dan wordt die maand
          overgeslagen.
        </p>
      ) : null}
      <TextArea
        label="Instructies (optioneel)"
        value={values.instructions}
        onChange={(instructions) => patch({ instructions })}
        rows={3}
        placeholder="Bijvoorbeeld: innemen met water tijdens het ontbijt"
      />
      <div className="caregiver-actions">
        <Button variant="primary" loading={saving} onClick={submit}>
          {submitLabel}
        </Button>
        <Button variant="ghost" disabled={saving} onClick={onCancel}>
          Annuleren
        </Button>
      </div>
    </div>
  )
}
